import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, Bell } from "lucide-react";
import ChatReminderForm from "@/components/ChatReminderForm";
import { trackChatStart, trackChatMessage } from "@/utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MASKY_IMG = "https://customer-assets.emergentagent.com/job_1abea0ec-cded-4678-a29f-de28c813ea65/artifacts/aeku2q6d_masky%20front%20on.png";

function getSessionId() {
  let id = sessionStorage.getItem("masky_chat_session");
  if (!id) {
    id = "masky_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem("masky_chat_session", id);
  }
  return id;
}

const DATE_QUESTION = /\b(date|dates|when|upcoming|next|future|holiday|holidays|half[- ]?term|christmas|easter|summer|october|february|may|june|july|august|december|booking open|remind|reminder|available|availability)\b/i;

function ReminderOffer({ onChat, onJump, testPrefix }) {
  return (
    <div className="bg-[#0A1A2F] rounded-2xl p-3 space-y-2 w-full" data-testid={`${testPrefix}-offer`}>
      <p className="text-white text-xs font-['Lato'] flex items-center gap-1.5">
        <Bell size={14} className="text-[#D4A857]" /> Want me to tell you when booking opens?
      </p>
      <div className="flex gap-2">
        <button onClick={onChat} className="flex-1 bg-[#D4A857] text-[#0A1A2F] text-xs font-bold py-2 rounded-lg" data-testid={`${testPrefix}-signup-in-chat`}>
          Sign up here
        </button>
        <button onClick={onJump} className="flex-1 border border-white/40 text-white text-xs font-bold py-2 rounded-lg hover:bg-white/10" data-testid={`${testPrefix}-get-reminders`}>
          Get booking reminders
        </button>
      </div>
    </div>
  );
}

export default function AskMaskyChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi there! I'm Masky, your friendly fencing guide! Ask me anything about the 5-Day Fencing Holiday Pass, what to expect, or how fencing can benefit your child." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const offered = useRef(false);

  const jumpToReminders = () => {
    setIsOpen(false);
    document.querySelector("#reminders")?.scrollIntoView({ behavior: "smooth" });
  };

  const openChatForm = () => {
    setMessages(prev => [...prev.filter(m => m.role !== "form"), { role: "form" }]);
  };

  const onReminderDone = (name, holidays) => {
    setMessages(prev => [...prev.filter(m => m.role !== "form"), {
      role: "bot",
      text: `Brilliant${name ? ", " + name : ""}! You're on the list for ${holidays.join(", ")}. I'll make sure you hear as soon as booking opens!`,
    }]);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: "user", text }]);
    setInput("");
    setLoading(true);
    if (messages.length <= 1) trackChatStart();
    trackChatMessage();

    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId.current }),
      });

      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      const offer = !offered.current && DATE_QUESTION.test(text);
      if (offer) offered.current = true;
      setMessages(prev => [...prev, { role: "bot", text: data.response }, ...(offer ? [{ role: "offer" }] : [])]);
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "Oops! I had trouble with that. Please try again or use the contact form below." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div
          className="fixed bottom-24 left-4 sm:left-6 w-[340px] sm:w-[380px] bg-white rounded-2xl shadow-2xl z-[70] flex flex-col overflow-hidden border border-gray-200"
          style={{ maxHeight: "min(520px, calc(100vh - 140px))" }}
          data-testid="masky-chat-panel"
        >
          {/* Header */}
          <div className="bg-[#0A1A2F] px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <img src={MASKY_IMG} alt="Masky" className="w-10 h-10 object-contain masky-glow-sm" />
            <div className="flex-1">
              <p className="text-white font-bold text-sm font-['Montserrat']">Ask Masky</p>
              <p className="text-white/60 text-xs font-['Lato']">Your fencing questions answered</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="chat-close-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="chat-messages">
            {messages.map((msg, i) => msg.role === "offer" ? (
              <ReminderOffer key={i} onChat={openChatForm} onJump={jumpToReminders} testPrefix="chat-reminder" />
            ) : msg.role === "form" ? (
              <ChatReminderForm key={i} onDone={onReminderDone} />
            ) : (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed font-['Lato'] ${
                    msg.role === "user"
                      ? "bg-[#0A1A2F] text-white rounded-br-sm"
                      : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <Loader2 size={16} className="animate-spin text-[#D4A857]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200 bg-white flex-shrink-0">
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="flex gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Masky a question..."
                className="flex-1 bg-gray-100 rounded-full px-4 py-2.5 text-sm font-['Lato'] outline-none focus:ring-2 focus:ring-[#D4A857]/50 text-gray-800 placeholder-gray-400"
                disabled={loading}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-[#D4A857] text-[#0A1A2F] w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#c49a4d] transition-colors disabled:opacity-40"
                data-testid="chat-send-btn"
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Floating Bubble */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-4 left-4 sm:left-6 z-[70] w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
          isOpen
            ? "bg-[#0A1A2F] hover:bg-[#0D2240] rotate-0"
            : "bg-[#D4A857] hover:bg-[#c49a4d] animate-bounce-subtle"
        }`}
        data-testid="masky-chat-bubble"
      >
        {isOpen ? (
          <X size={22} className="text-white" />
        ) : (
          <MessageCircle size={22} className="text-[#0A1A2F]" />
        )}
      </button>
    </>
  );
}
